import {
  filtersToGraphQLFilters,
  IPageFilterProps,
  IProperty,
  makeDynamicWorkboardPageStore,
  PageFiltersRenderer,
  PageViewModeEnum,
  PerformanceWorkboardCountKey,
  PerformanceWorkboardKey,
  performanceWorkFilterOptionsQuery,
  SortModel,
  workV4Query,
  queryClient,
  IWork,
} from '@diana/core';
import { useQuery } from '@tanstack/react-query';
import { omit } from 'lodash';
import { ReactElement } from 'react';

export const WorkboardFilterRenderer = ({ filterProps }: { filterProps: IPageFilterProps }) => {
  const {
    filter: { property },
    filtersValues,
    flowOriginId,
  } = filterProps;
  const useWorkboardPageStore = makeDynamicWorkboardPageStore(flowOriginId || '');
  const availableFilters = useWorkboardPageStore((s) => s.availableFilters);
  const properties = useWorkboardPageStore((s) => s.properties);
  const flowId = useWorkboardPageStore((s) => s.flow?.id);
  const flowType = useWorkboardPageStore((s) => s.flow?.flowType);
  const stepsLength = useWorkboardPageStore((s) => s.steps?.length);
  const worksLength = useWorkboardPageStore((s) => s.works?.length);

  const filterProperties = filtersToGraphQLFilters({
    filters: availableFilters,
    filtersValues: omit(filtersValues, property.key), // prevent passing filtered values for the the said current filter key
  });

  const optionsQuery = performanceWorkFilterOptionsQuery.get({
    key: property.key,
    displayNamePath: property.displayNamePath,
    flowTypes: [flowType],
    isDefaultProperty: property.isDefaultProperty,
    valuePaths: property.valuePaths,
    flowOriginIds: [flowOriginId!],
    flowIds: [flowId],
    showCompleted: true,
    showDeleted: false,
    showInProgress: true,
    filterType: property.type!,
    additionalProperties: properties.map((p) => ({
      key: p.key,
      displayName: p.displayName,
      isExpandedProperty: false,
      valuePath: p.valuePath,
      valuePaths: p.valuePaths,
      displayNamePath: p.displayNamePath ?? null,
    })),
    filterProperties,
  });

  const { data, isFetching } = useQuery({
    queryKey: [...optionsQuery.key, flowType, flowId, stepsLength, worksLength, filterProperties],
    queryFn: optionsQuery.fn,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  return <PageFiltersRenderer filterProps={filterProps} data={data} isLoading={isFetching} />;
};

export const createPropertyToFilter = () => {
  return (property: IProperty) => {
    return {
      property,
      renderFilter: (props: IPageFilterProps): ReactElement => {
        return <WorkboardFilterRenderer filterProps={props} />;
      },
    };
  };
};

export const getWorkboardGraphQLWorksQueryKey = (
  flowId: string,
  graphQLFilters: string,
  viewMode?: PageViewModeEnum,
  activeStepIds?: string[],
  sortModel?: SortModel,
) => [
  PerformanceWorkboardKey,
  flowId,
  graphQLFilters,
  // Be careful when you change the order or value here, as socket updates are applied based on these keys
  JSON.stringify(activeStepIds),
  JSON.stringify(sortModel),
  viewMode,
];

export const getWorkboardGraphQLWorksCountQueryKey = (params: {
  flowId: string;
  activeStepIds?: string[];
  graphQLFilters: string;
}) => [
  PerformanceWorkboardCountKey,
  params.flowId,
  params.graphQLFilters,
  JSON.stringify(params.activeStepIds),
];

export const getOfflineFilteredWorks = ({
  works,
  isOfflineModeEnabled,
  isOnline,
}: {
  works: IWork[];
  isOfflineModeEnabled: boolean;
  isOnline: boolean;
}) => {
  if (isOnline || !isOfflineModeEnabled) return works;
  return works.filter((work) => {
    const workQuery = workV4Query.getWork({ workId: work.id });
    const workDataQuery = workV4Query.getWorkData({ workId: work.id });
    const hasWork = Boolean(queryClient.getQueryData(workQuery.key));
    const hasWorkData = Boolean(queryClient.getQueryData(workDataQuery.key));
    return hasWork && hasWorkData;
  });
};
